/**
 * The accord between the three Sessions screens (§12.1 to §12.3) - every
 * `data-testid` that more than one file reads, and the props the shared blocks
 * take.
 *
 * Same patron as `protocoles/contracts.ts` and `experiences/contracts.ts`, and
 * for the same reason: the e2e suite and the capture scripts do not compile
 * this package, so they RECOPY these strings. A mark declared in the component
 * that draws it is a mark those two learn about by breaking.
 *
 * ## Two prefixes that look alike and are not
 *
 * `session-` belongs to the PERSON's screens (`components/session/contracts.ts`):
 * the door, the player, the bubbles. `sessions-` belongs to the OPERATOR's, the
 * ones in this folder. The plural is the whole difference, so it is written
 * here rather than left to be noticed: one is somebody playing a session, the
 * other is somebody reading all of them.
 *
 * `sessions-liste` and `session-ligne` keep the exact strings the minimal list
 * of #60 already used, and they move here from the player's contracts rather
 * than being re-declared: they name an administration table, `session.spec.ts`
 * copies the row's value, and two declarations of one string is how the two
 * come to disagree.
 */

import { CONVERSATION_LAYERS, type ConversationLayer } from '@matter_extractor/shared';

/* ------------------------------------------------------------- the marks */

export const SESSIONS_TEST_IDS = {
  /* --- la liste (§12.1) --- */
  /** The table itself. Kept from #60: an e2e spec already reads this string. */
  list: 'sessions-liste',
  /** One line, with `data-session-id` and `data-focused` for the `?session=`. */
  row: 'session-ligne',
  /**
   * The pill of the RETAINED noyau on a line (noyau.md §9.2, #152), written by
   * its triplet with `data-noyau`; absent on a line without one.
   */
  rowNoyau: 'session-ligne-noyau',
  /**
   * The dashed pill « attendu SGP » beside it, on the line of a replay of an NPC whose expected
   * noyau is a triplet (npc.md §26.6, H37; #216 lot M), with `data-noyau` (the expected one),
   * `data-verdict` (absent without a comparison) and `data-provenance` (`played` or `current`).
   * Absent on every other line.
   */
  rowExpected: 'session-ligne-attendu',
  /** The person's name under the time of a line (§9.2 of the refonte, H75). */
  rowPerson: 'session-ligne-personne',
  /**
   * The search of Sessions (recherche-sessions.md section 4, issue #213): the field on the
   * line of the tabs (the mark is on the `<input>`), the sentence under it when one character
   * is typed, and the polite region that says how many sessions were found.
   */
  search: 'sessions-recherche',
  searchHint: 'sessions-recherche-aide',
  searchAnnouncement: 'sessions-recherche-annonce',
  /** The empty state of a search that retains nothing, and its « Réinitialiser la recherche ». */
  searchEmpty: 'sessions-recherche-vide',
  searchReset: 'sessions-recherche-reinitialiser',
  /**
   * The line « trouvé dans ... » under the referents of a retained line (section 5), with
   * `data-count` (the places in all) and `data-open`; each place is a `rowFoundLine` with its
   * `data-kind`; a reply's words are the link `rowFoundTurn`; the button that unfolds the others
   * is `rowFoundMore` and folds them back as `rowFoundLess`; `rowFoundRest` is « et N autres ».
   */
  rowFound: 'session-ligne-trouve',
  rowFoundLine: 'session-ligne-trouve-ligne',
  rowFoundTurn: 'session-ligne-trouve-tour',
  rowFoundMore: 'session-ligne-trouve-plus',
  rowFoundLess: 'session-ligne-trouve-replier',
  rowFoundRest: 'session-ligne-trouve-reste',
  /** The bar of the four visible filters, and the button that opens the rest. */
  filters: 'sessions-filtres',
  /** The panel of « Plus de filtres »: protocol, persona, criterion, and the retained noyau. */
  moreFilters: 'sessions-filtres-plus',
  /** One removable chip above the table. */
  filterChip: 'sessions-filtre-pastille',
  /** « Afficher la corbeille », which SWAPS the list rather than adding to it. */
  trashToggle: 'sessions-corbeille',
  /*
    Two marks and not one. They used to share `sessions-supprimer` - the BUTTON
    of a row on the list, the BOX on the parcours - so a spec that clicked the
    mark and then waited for it found the same string naming two different
    nodes. The button keeps the value `sessions.spec.ts` and
    `screenshots-sessions.mjs` already recopy; the box takes the one the list's
    own dialog already carried.
  */
  /** « Supprimer » on one row of the list. */
  rowDelete: 'sessions-supprimer',
  /** « Restaurer » on one row of the trash. */
  rowRestore: 'sessions-restaurer',
  /** The confirmation box, wherever the gesture was started from. */
  deleteDialog: 'sessions-boite-suppression',
  /** The two tabs, Liste and Matière. */
  tabs: 'sessions-onglets',
  /** The Matière tab of §12.4, which replaced a seam of the same name (#72). */
  matiere: 'sessions-matiere',

  /* --- le parcours (§12.2) --- */
  /** The whole parcours screen. */
  walk: 'sessions-parcours',
  /** The row of figures at the head of a screen. */
  tiles: 'sessions-chiffres',
  /** One traversed page, with `data-page-id`. */
  walkPage: 'sessions-parcours-page',
  /** The protocol block - the only one that carries the bleu nuit border. */
  walkProtocol: 'sessions-parcours-protocole',
  /** One step of the annotated chain, with `data-step-id`. */
  chainStep: 'sessions-chaine-etape',
  /**
   * The block « Détermination du noyau » (noyau.md §9.1, #152), after the
   * protocol's, with `data-state`.
   */
  walkNoyau: 'sessions-parcours-noyau',
  /** One of the six notes, sorted, with `data-noyau` and `data-place` (principal, second). */
  walkNoyauScore: 'sessions-noyau-note',
  /**
   * The button of the block's header (#219): « Voir les deux passes ▾ » then « Replier ▴ », with
   * `aria-expanded`. The block arrives FOLDED; the block itself carries `data-open`.
   */
  walkNoyauToggle: 'sessions-noyau-replier',
  /** The line a folded block says in place of its body: the two notes, the model, the time, the cost. */
  walkNoyauSummary: 'sessions-noyau-resume',
  /**
   * The band « Face à l'attendu » of the block (npc.md §26.3, issue #216, lot K), between the
   * facts of the block and its six notes, with `data-verdict` (same, near, mirror, none; absent
   * without a comparison), `data-state` (`compared`, `no-found`, `out-of-base`),
   * `data-provenance` (`played`, `current`) and `data-determination` (the state of the
   * determination, absent without one). Never drawn on a trial, a real session or a replay with
   * no expected noyau.
   */
  walkNoyauExpected: 'sessions-noyau-attendu',
  /** Where the expected noyau came from: « de Nicolas Hulot v5 », a link to that version of the NPC. */
  walkNoyauExpectedSource: 'sessions-noyau-attendu-source',
  /** One level of the band (Pourquoi, Comment, Quoi), with `data-level` and `data-holds`. */
  walkNoyauExpectedLevel: 'sessions-noyau-attendu-niveau',
  /** The pill of the verdict, or of what there is to say instead of one. */
  walkNoyauExpectedVerdict: 'sessions-noyau-attendu-verdict',
  /** The two noyaux in full words. */
  walkNoyauExpectedNames: 'sessions-noyau-attendu-noms',
  /** Where the expected noyau falls: its rank, its gap, the cards. */
  walkNoyauExpectedPlace: 'sessions-noyau-attendu-place',
  /** The line of the principal, only when it differs from the retained noyau. */
  walkNoyauExpectedPrincipal: 'sessions-noyau-attendu-principal',
  /** The pill « attendu » on the row of the expected noyau among the six notes. */
  walkNoyauScoreExpected: 'sessions-noyau-note-attendu',
  /** One attempt of the analyst on a determination that is not rendered, with its cause. */
  walkNoyauAttempt: 'sessions-noyau-essai',
  /** The button that READS the analyst's served entry, and one entry it reveals. */
  walkNoyauPrompts: 'sessions-noyau-entrees',
  walkNoyauPrompt: 'sessions-noyau-entree',
  /**
   * The line « Lecture : <protocole> v<N> · prompt <version> · empreinte <8 hex> »
   * of the block (noyau.md §9.1, N38, N40; issue #154), with `data-reading`
   * (`protocol`, `generic`, or `unknown` when the frozen protocol does not read).
   */
  walkNoyauReading: 'sessions-noyau-lecture',
  /** The protocol and its version on that line, a link to the reading of the FROZEN version. */
  walkNoyauReadingLink: 'sessions-noyau-lecture-lien',
  /** A fingerprint in fixed pitch - on the line, and on each attempt of a failure. */
  walkNoyauFingerprint: 'sessions-noyau-empreinte',
  /** The section of a traversed page that carries a Noyau block, with `data-mode`. */
  walkNoyauPage: 'sessions-parcours-bloc-noyau',
  /** One card of a choice, in the order shown, with `data-noyau`, `data-role`, `data-taken`. */
  walkNoyauCard: 'sessions-parcours-carte',
  /** The portrait served on a page of portrait. */
  walkNoyauPortrait: 'sessions-parcours-portrait',
  /**
   * The gap threshold of the first choice (noyau.md §3.8, §9.1, N74; planche
   * `EcartSessionParcours`, #169): the fact « seuil 5 points » or « sans seuil »
   * on the line of the block, with `data-max-gap` (absent without a threshold);
   * the outlined pill « retenu » on the card of the retained noyau; and the
   * line under « Carte prise », with `data-state` (`decides`, `no-effect`,
   * `before-threshold`).
   */
  walkNoyauThreshold: 'sessions-parcours-seuil',
  walkNoyauRetained: 'sessions-parcours-retenu',
  walkNoyauGap: 'sessions-parcours-ecart',
  /**
   * The pill « éliminé » on the card of a noyau the levels eliminated, on a page of
   * first choice (analyste.md §8.5, #196): the second, when it was one, so that « sans
   * effet : le second est éliminé » has a card to point at.
   */
  walkNoyauCardEliminated: 'sessions-parcours-elimine',
  /**
   * The line a traversed page says its facts on (#223): the values typed there, the card of a
   * choice, the button that left it - one line, the sentence wrapping when it is long.
   */
  walkPageDetail: 'sessions-parcours-page-detail',
  /** The words of the exit of a traversed page (« Quittée par le bouton ... »), wherever it is drawn. */
  walkPageExit: 'sessions-parcours-page-sortie',
  /**
   * The summary of a page of CHOICE of the noyau (#223), on the line of the page: « Premier choix :
   * carte prise PGS, sans effet (écart 14 > 5) · retenu PSG », with `data-mode`.
   */
  walkNoyauPageSummary: 'sessions-parcours-bloc-noyau-resume',
  /**
   * The button that unfolds the two cards of a page of choice (#223): « Voir les cartes ▾ » then
   * « Replier ▴ », with `aria-expanded`. The cards arrive FOLDED.
   */
  walkNoyauPageToggle: 'sessions-parcours-cartes-bascule',
  /**
   * The button that unfolds the rest of a portrait served (#223): « Lire tout le portrait ▾ » then
   * « Replier le portrait ▴ ». Absent on a portrait that is not longer than what is shown.
   */
  walkNoyauPortraitToggle: 'sessions-parcours-portrait-bascule',
  /*
    The block « Détermination du noyau » of the analyst in TWO PASSES (analyste.md
    §11.1, issue #196, lot D). The block itself keeps `walkNoyau` and now carries
    `data-analysis`: `two-passes` when the determination stores an analysis - its
    reasoning, or the header alone when it read nothing (pending, no extract,
    failed) -, `direct` when it was written by the analyst of before #196 (no
    analysis and calls with no pass: the grey pill below), and no attribute on one
    of before that read nothing either (an old « sans extrait »). The marks inside
    are shared with a TRIAL's block, found within their own block.
  */
  /** The pill « lecture directe, avant #196 » of a determination written before the two passes. */
  walkNoyauBefore: 'sessions-noyau-avant',
  /** « Méthode vN » on the line of the instrument, with `data-version-id`. */
  walkNoyauMethod: 'sessions-noyau-methode',
  /**
   * One foldable section of the reasoning, with `data-section` (`passe-1`,
   * `niveaux`, `elimination`, `passe-2`, `notes`) and `data-open`; and the button
   * that folds it, with `aria-expanded`.
   */
  walkNoyauSection: 'sessions-noyau-section',
  walkNoyauSectionToggle: 'sessions-noyau-section-bascule',
  /** One objective of the first pass, with `data-objective-id` and `data-level`. */
  walkNoyauFunction: 'sessions-noyau-fonction',
  /** One of the three levels, with `data-level` and `data-state`; and the sentence of a conflict. */
  walkNoyauLevel: 'sessions-noyau-niveau',
  walkNoyauConflict: 'sessions-noyau-conflit',
  /** A candidate the levels left, and a noyau they eliminated, each with `data-noyau`. */
  walkNoyauCandidate: 'sessions-noyau-candidat',
  walkNoyauEliminated: 'sessions-noyau-elimine',
  /** One candidate of the second pass, with `data-noyau`; and the count of passages set aside. */
  walkNoyauStyle: 'sessions-noyau-style',
  walkNoyauDiscarded: 'sessions-noyau-ecartes',
  /** The yellow line of a style that could not be read: the second pass failed twice. */
  walkNoyauStyleAbsent: 'sessions-noyau-style-absent',
  /** The sentence that says how the style was combined, once for the second pass read several times (#229). */
  walkNoyauReadingsIntro: 'sessions-noyau-lectures-phrase',
  /** The yellow line of a style read several times of which a part failed, with `data-failed` (#229). */
  walkNoyauReadingsFailed: 'sessions-noyau-lectures-echec',
  /** The line of the readings under a candidate: « 3 lectures : 79, 82 et 81, moyenne 81 » (#229). */
  walkNoyauReadingsLine: 'sessions-noyau-lectures-ligne',
  /** The reading that carries the note of a candidate, open, with `data-run` (#229). */
  walkNoyauReadingCarrier: 'sessions-noyau-lecture-porteuse',
  /** Another reading of a candidate, folded on a line, with `data-run` and `data-open` (#229). */
  walkNoyauReadingFold: 'sessions-noyau-lecture-pliee',
  /** The button that unfolds it, with `aria-expanded` (#229). */
  walkNoyauReadingToggle: 'sessions-noyau-lecture-pliee-bascule',
  /** The pill « 2 lectures sur 3 » of the header, visible folded (#229). */
  walkNoyauReadingsPill: 'sessions-noyau-lectures-pastille',
  /**
   * What a determination settled `failed` WITHOUT a call says, with `data-reason`
   * (`method-missing`, `section-incomplete`): a session started before #196.
   */
  walkNoyauReason: 'sessions-noyau-raison',
  /**
   * The section « Lecture à blanc » of the parcours (noyau.md §9.4, #164): a
   * TRIAL of the analyst, shown, and kept with the session once settled (#167).
   */
  noyauTrial: 'sessions-lecture-a-blanc',
  /** Its one button, « Lancer l’analyste » then « Relancer l’analyste ». */
  noyauTrialRun: 'sessions-lecture-a-blanc-lancer',
  /** The sentence of a refusal, with `data-code`. */
  noyauTrialRefusal: 'sessions-lecture-a-blanc-refus',
  /** The block of a trial - the determination's, under its own marks - with `data-state`. */
  noyauTrialBlock: 'sessions-lecture-a-blanc-bloc',
  /** One of its six notes, sorted, with `data-noyau` and `data-place`. */
  noyauTrialScore: 'sessions-lecture-a-blanc-note',
  /** The fold button of a trial's block (#219), the determination's pattern under its own mark. */
  noyauTrialToggle: 'sessions-lecture-a-blanc-replier',
  /** The line a folded trial's block says in place of its body. */
  noyauTrialSummary: 'sessions-lecture-a-blanc-resume',
  /** The button that shows what the trial served, held with it rather than read from the base. */
  noyauTrialPrompts: 'sessions-lecture-a-blanc-entrees',
  /** The list of the session's trials, newest first, once it keeps more than one (#167). */
  noyauTrialList: 'sessions-lecture-a-blanc-liste',
  /**
   * One trial of that list - a button, with `data-trial` (its id) and
   * `data-state`, and `aria-current` on the one the block draws.
   */
  noyauTrialListItem: 'sessions-lecture-a-blanc-essai',
  /**
   * The selector « Source » of the blank analyst (relecture.md §11): the wrapper of
   * the package `Select` (its `role="combobox"` is inside), with `data-source` -
   * `session`, `rangement-trial` or `review` - the kind of the CHOSEN source. The
   * value of an option is the choice's key.
   */
  noyauTrialSource: 'sessions-lecture-a-blanc-source',
  /** The line « Matière : » of a trial's block, with `data-source` (the kind it read). */
  noyauTrialMatter: 'sessions-lecture-a-blanc-matiere',
  /** What a row of the list says of its trial's source, with `data-source`. */
  noyauTrialListSource: 'sessions-lecture-a-blanc-essai-source',
  /** The fingerprint of the INSTRUMENT a row of the list was read with (#196, K11). */
  noyauTrialListFingerprint: 'sessions-lecture-a-blanc-essai-empreinte',
  /**
   * The door a refusal of the section Analyste offers (`analyst-levels-missing`,
   * `analyst-reading-missing`): a link to that section of the protocol (#196).
   */
  noyauTrialRefusalLink: 'sessions-lecture-a-blanc-refus-lien',

  /*
    The rangement of the matter (scenario-succes.md §7, issue #177): ids ONLY, the
    components are the lot of Sessions's. Read by the e2e suite and the capture
    scripts, which recopy them.

    The routes the blank rangement serves are written beside their shapes, in
    `packages/shared/src/schemas/rangement-session.ts`.
  */
  /** The block « Rangement de la matière » (§7.1), under the protocol's, with `data-state`. */
  walkRangement: 'sessions-parcours-rangement',
  /** Its state line: rendu, en échec, aucun, sans matière, en cours (§7.2), with `data-state`. */
  walkRangementState: 'sessions-parcours-rangement-etat',
  /** The table of three columns - the objective, the scribe, the ranger - one row per target. */
  walkRangementTable: 'sessions-parcours-rangement-table',
  /** One row of it, with `data-objective` (the objective's id). */
  walkRangementRow: 'sessions-parcours-rangement-ligne',
  /** The cell « Scribe, pendant l’entretien » of a row: its status and its living extracts. */
  walkRangementScribeCell: 'sessions-parcours-rangement-cellule-scribe',
  /** The cell « Rangeur, lu par l’analyste » of a row, with `data-state` (portée, imprécise, vide). */
  walkRangementRangerCell: 'sessions-parcours-rangement-cellule-rangeur',
  /** The formula of a cell, marked « reformulation ». */
  walkRangementFormula: 'sessions-parcours-rangement-formule',
  /** The list of the passages read and put nowhere. */
  walkRangementDiscarded: 'sessions-parcours-rangement-ecartes',
  /** One of them, with `data-reason` and `data-turn`. */
  walkRangementDiscardedRow: 'sessions-parcours-rangement-ecarte',
  /** « 1 passage rangé deux fois : gardé en 1. …, retiré de 6. … », in the facts (relecture.md §12). */
  walkRangementDuplicates: 'sessions-parcours-rangement-doublons',
  /** The button « Voir l’entrée servie au rangeur », read on a click. */
  walkRangementPrompts: 'sessions-parcours-rangement-entrees',
  /** The fold button of the block's header (#219): « Voir la matière ▾ » then « Replier ▴ ». */
  walkRangementToggle: 'sessions-parcours-rangement-replier',
  /** The line a folded block says in place of its body: what it came to, the model, the time, the cost. */
  walkRangementSummary: 'sessions-parcours-rangement-resume',

  /** The section « Rangement à blanc » (§7.3): a trial of the ranger on the conversation of the session. */
  rangementTrial: 'sessions-rangement-a-blanc',
  /** Its one button, « Lancer le rangeur ». */
  rangementTrialRun: 'sessions-rangement-a-blanc-lancer',
  /** The sentence of a refusal (409), with `data-code`. */
  rangementTrialRefusal: 'sessions-rangement-a-blanc-refus',
  /** The block of a trial - the ranger's column, marked « à blanc, vN » - with `data-state`. */
  rangementTrialBlock: 'sessions-rangement-a-blanc-bloc',
  /** The list « Essais de cette session », newest first. */
  rangementTrialList: 'sessions-rangement-a-blanc-liste',
  /** One trial of that list - a button, with `data-trial` and `data-state`, `aria-current` on the one drawn. */
  rangementTrialListItem: 'sessions-rangement-a-blanc-essai',
  /** The fold button of a ranger trial's block (#219). */
  rangementTrialToggle: 'sessions-rangement-a-blanc-replier',
  /** The line a folded ranger trial's block says in place of its body. */
  rangementTrialSummary: 'sessions-rangement-a-blanc-resume',
  /**
   * The group « Essais à blanc sur cette session » (#220), after the fil of the pages: its
   * title, its sentence, then the sections « Lecture à blanc » and « Rangement à blanc ».
   */
  walkTrials: 'sessions-essais-a-blanc',

  /* --- the two blocks of the page protocole, drawn as ONE pattern (#224) --- */
  /** The bleu nuit rule that hangs a block under the protocol card (it replaced the grey edge and its sentence). */
  walkBlockEdge: 'sessions-parcours-bloc-trait',
  /** The small label of a block's header, « Bloc de la page 5 » - the rank of the protocol page. */
  walkBlockLabel: 'sessions-parcours-bloc-etiquette',
  /**
   * The foot of a block of the session (#224): where it came from and the tool of its dry trial.
   * `data-trial` says whether the foot carries the trial (`tools`), the vitrine's note
   * (`note`) or nothing but the origin (`none`).
   */
  walkBlockFoot: 'sessions-parcours-bloc-pied',
  /** The sentence of origin in that foot: « Rangée en fond, à la clôture du protocole ». */
  walkBlockOrigin: 'sessions-parcours-bloc-origine',
  /** The three columns a FOLDED determination says under its header (#224), with `data-columns`. */
  walkNoyauBrief: 'sessions-noyau-synthese',
  /** One level of its first column, with `data-level` and `data-state`. */
  walkNoyauBriefLevel: 'sessions-noyau-synthese-niveau',
  /** One candidate of its second column, with `data-noyau`. */
  walkNoyauBriefStyle: 'sessions-noyau-synthese-style',
  /** The notes of the readings of a candidate and the one that carries its note, when the style was read several times (#229). */
  walkNoyauBriefReadings: 'sessions-noyau-synthese-lectures',
  /** One of the six notes of its third column, with `data-noyau` (never `walkNoyauScore`: the open block owns that one). */
  walkNoyauBriefNote: 'sessions-noyau-synthese-note',
  /** The pills a FOLDED rangement says under its header, one per objective target (#224). */
  walkRangementObjectives: 'sessions-parcours-rangement-objectifs',
  /** One of them, with `data-objective` and `data-state` (portée, imprécise, vide). */
  walkRangementObjective: 'sessions-parcours-rangement-objectif',
  /** « ⓘ Comment le lire » beside the title « Le parcours » (#224), with `aria-expanded`. */
  walkLegendToggle: 'sessions-parcours-legende-bouton',
  /** The note it opens (the legend of the hour of arrival). */
  walkLegend: 'sessions-parcours-legende',
  /** The banner a session in the trash is read under. */
  trashedBanner: 'sessions-corbeille-bandeau',

  /* --- the two tabs under the eight figures (§12.2, #172) --- */
  /**
   * The bar Parcours / Variables, and each of its buttons, suffixed with the
   * tab (`sessions-parcours-onglet-variables`), `aria-current` on the open one.
   */
  walkTabs: 'sessions-parcours-onglets',
  walkTab: 'sessions-parcours-onglet',
  /**
   * The sommaire of the Parcours tab (#221): the column to the right of the walk from `xl` up,
   * with `data-count` (its entries). Never drawn on Variables, nor under `xl`.
   */
  walkToc: 'sessions-sommaire',
  /**
   * One entry of it, with `data-anchor` (the id it leads to), `data-kind` (page, protocol, block,
   * section) and `aria-current="location"` on the one in view.
   */
  walkTocEntry: 'sessions-sommaire-entree',
  /** The Variables tab: the count line, the table and its legend. */
  variables: 'sessions-variables',
  /** « 6 remplies · 6 non remplies », with `data-filled` and `data-unfilled`. */
  variablesCount: 'sessions-variables-compte',
  /**
   * One line of the inventory, with `data-key`, `data-kind`, `data-filled`
   * and, on an unfilled one, `data-reason` (the contract's value).
   */
  variableRow: 'sessions-variable',
  /**
   * The page of a line: a button with `data-visit` (0-based, the index of the
   * card it leads to), or a grey name with no `data-visit` on a page never
   * reached. A walk card carries the same `data-visit`, and `data-flash` while
   * it is ringed on the way back from the tab.
   */
  variablePage: 'sessions-variable-page',
  /** « Cette expérience ne déclare aucune variable. » */
  variablesEmpty: 'sessions-variables-vide',

  /* --- « Exporter en », on the parcours AND the conversation (§12.6, #173) --- */
  /** The trigger, with `aria-expanded`; truly `disabled` on a session in the trash. */
  exportTrigger: 'sessions-exporter',
  /** The open panel and its two groups. */
  exportPanel: 'sessions-exporter-menu',
  /** One entry, with `data-format` (`pdf`, `html`, `md`, `json`); the PDF one with `aria-busy`. */
  exportEntry: 'sessions-exporter-entree',
  /** The sentence of a PDF that could not be prepared, inside the panel. */
  exportError: 'sessions-exporter-erreur',
  /** « Restaurez la session pour l’exporter. », under the disabled trigger. */
  exportTrashReason: 'sessions-exporter-raison',

  /* --- « Relecture », on the parcours AND the conversation (relecture.md §3.2, #187) --- */
  /**
   * The entry to the review, with `data-state` (`open` - a link - or `disabled`:
   * a really disabled button). Absent on a frozen experience with no protocol page.
   */
  reviewButton: 'sessions-relecture-bouton',
  /** « La relecture s’ouvre quand la session est finie. », under the disabled button. */
  reviewButtonReason: 'sessions-relecture-bouton-raison',
  /** « relecture · patrick, alice » under the experience of a row of the list (§13). */
  rowReviewMark: 'session-ligne-relecture',

  /* --- Interrompre une session (interruption.md, issue #199) --- */
  /**
   * The link « Interrompre la session » in the sentence under the disabled « Relecture »,
   * on the parcours AND the conversation. Absent on a session that is over, in the trash,
   * of the prod (on the atelier), or whose experience has no protocol page.
   */
  interruptLink: 'sessions-interrompre-lien',
  /** The sentence of a session of the prod on the atelier, with its link « l'ouvrir sur la prod ». */
  interruptProdSentence: 'sessions-interrompre-prod',
  interruptProdLink: 'sessions-interrompre-prod-lien',
  /** The confirmation box, and the three things it says. */
  interruptDialog: 'sessions-boite-interruption',
  /** « il y a 2 h 14 (09:58) », with `data-ago`; the row `où` is `interruptWhere`, absent without a position. */
  interruptWhen: 'sessions-interruption-quand',
  interruptWhere: 'sessions-interruption-ou',
  /** The red box of a refusal; its sentence carries `data-code` (`none`: a failure with no code). */
  interruptRefusal: 'sessions-interruption-refus',
  /** The pastille of the status on the head of the parcours (« en cours », « interrompue »...). */
  headStatus: 'sessions-statut-tete',
  /** The sentence under the title of the parcours of an interrupted session: « interrompue à 12:12 par alice ». */
  headInterrupted: 'sessions-interrompue-ligne',

  /* --- « Rejeu par Claude » (rejeu.md §12, issue #193) --- */
  /**
   * The violet pill « rejeu · strict » of a row of the list, in the column Lien,
   * with `data-mode` (`strict`, `completed`); and the label of the source under it.
   */
  rowReplay: 'session-ligne-rejeu',
  rowReplaySource: 'session-ligne-rejeu-source',
  /**
   * The NPC of a replay of an NPC (npc.md §13.1, issue #210): the link to its fiche, replacing the
   * label of the source on the first line; « joué avant les NPC » after it on a replay attached
   * afterwards; and the series under it.
   */
  rowReplayNpc: 'session-ligne-rejeu-npc',
  rowReplaySeeded: 'session-ligne-rejeu-avant-npc',
  rowReplaySeries: 'session-ligne-rejeu-serie',
  /**
   * The eleventh filter, « NPC », behind « Plus de filtres », and its help. The mark is on the
   * wrapper of the package `Select`, whose `role="combobox"` is inside it.
   */
  npcFilter: 'sessions-filtre-npc',
  npcFilterHelp: 'sessions-filtre-npc-aide',
  /**
   * The tenth filter, behind « Plus de filtres », and its sentence of help. The mark is on the
   * wrapper of the package `Select`, whose `role="combobox"` is inside it.
   */
  replayFilter: 'sessions-filtre-rejeu',
  replayFilterHelp: 'sessions-filtre-rejeu-aide',

  /* --- « Créer un NPC » on the head of the parcours (npc.md §4.2, §13.3, issue #210) --- */
  /** The gesture, with `data-state` (`open`, `disabled`) and, greyed, `data-reason`. */
  createNpc: 'sessions-creer-npc',
  /** The line under the open gesture, and the reason under the greyed one. */
  createNpcHint: 'sessions-creer-npc-aide',
  createNpcReason: 'sessions-creer-npc-raison',

  /* --- The origin of a session (vitrine.md section 8.2, issue #192) --- */
  /**
   * The « Origine » filter, in the always-visible row. The mark is on the wrapper of the package
   * `Select`, whose `role="combobox"` is inside it.
   */
  originFilter: 'sessions-filtre-origine',
  /** The mark « prod » on a row of the list, on the head of the parcours and on the head of the conversation. */
  rowOrigin: 'session-ligne-origine',
  headOrigin: 'sessions-origine',
  conversationOrigin: 'sessions-conversation-origine',
  /** The sentence under the title of the parcours of a session of the prod. */
  headOriginLine: 'sessions-origine-ligne',
  /**
   * The line under the title of the parcours (#218): « Noyau retenu » or « Lu à blanc », with
   * `data-kind` (`retained`, `trial`) and `data-noyau`. The person's name leads the title
   * (`headPerson`), the pill is a button that leads to the block (`headNoyauPill`), and the
   * words after the name (what retained it, the notes) are `headNoyauDetail`.
   */
  headNoyau: 'sessions-tete-noyau',
  headNoyauPill: 'sessions-tete-noyau-pastille',
  headNoyauDetail: 'sessions-tete-noyau-detail',
  headPerson: 'sessions-tete-personne',
  /**
   * The menu « Plus d'actions » of the head of the parcours (#225): its trigger (`headMenu`, with
   * `aria-expanded`) and the status it says after « Copier l'identifiant » (`headCopyStatus`, with
   * `data-state`: `idle`, `copied`, `failed`). The entries are read by role (`menuitem`).
   */
  headMenu: 'sessions-tete-menu',
  headCopyStatus: 'sessions-tete-copie',
  /** The same pill on the head of the parcours and on the head of the conversation, with `data-mode`. */
  replayHeadPill: 'sessions-rejeu-pastille',
  replayConversationPill: 'sessions-conversation-rejeu-pastille',
  /** The sentence « rejouée par Claude, sans lien d’entrée » under the title of the parcours. */
  replayHeadLine: 'sessions-rejeu-ligne',
  /**
   * The panel « Rejeu par Claude » between the head and the figures of the
   * parcours, with `data-mode`, `data-source-kind`; absent on an ordinary session.
   */
  replayPanel: 'sessions-rejeu',
  /**
   * The fold of the panel (#225): the line it says folded (`replaySummary`, with the link of the NPC
   * `replaySummaryNpcLink`) and « Détails ▾ » / « Masquer ▴ » (`replayToggle`, `aria-expanded`).
   */
  replaySummary: 'sessions-rejeu-resume',
  replaySummaryNpcLink: 'sessions-rejeu-resume-npc-lien',
  replayToggle: 'sessions-rejeu-details',
  /** « lancé par <opérateur> le <date>, depuis l'assistant MCP · <modèle> (déclaré) ». */
  replayStarted: 'sessions-rejeu-lance',
  /** The three facts: Mode, Source, Rejoue. */
  replayMode: 'sessions-rejeu-mode',
  replaySource: 'sessions-rejeu-source',
  /** « Rejoue », with `data-kind` (`none`, `linked`, `gone`); the link of a readable origin. */
  replayOrigin: 'sessions-rejeu-origine',
  replayOriginLink: 'sessions-rejeu-origine-lien',
  /** « Voir la source » and « Voir la consigne servie », with `aria-expanded`, and the boxes they unfold. */
  replaySourceToggle: 'sessions-rejeu-source-bouton',
  replaySourceText: 'sessions-rejeu-source-texte',
  replayInstructionToggle: 'sessions-rejeu-consigne-bouton',
  replayInstructionText: 'sessions-rejeu-consigne-texte',
  /**
   * The facts a replay of an NPC adds (npc.md §13.2, issue #210): the grid that holds them, the NPC
   * and its link, the series with its pass, the player with its two costs, the issue, and the Modèles
   * du dispositif; and the link to the fiche in the sentence « Pour rejouer ce cas ... ».
   */
  replayFacts: 'sessions-rejeu-faits',
  replayNpc: 'sessions-rejeu-npc',
  replayNpcLink: 'sessions-rejeu-npc-lien',
  replaySeries: 'sessions-rejeu-serie',
  replayPlayer: 'sessions-rejeu-joueur',
  replayIssue: 'sessions-rejeu-issue',
  replayModels: 'sessions-rejeu-modeles',
  replayAgainNpcLink: 'sessions-rejeu-rejouer-fiche',
  /** The line « Pour rejouer ce cas ... », the identifier on it, and « Copier ». */
  replayAgain: 'sessions-rejeu-rejouer',
  replayId: 'sessions-rejeu-identifiant',
  replayCopy: 'sessions-rejeu-copier',

  /* --- la conversation (§12.3) --- */
  /** The whole conversation screen. */
  conversation: 'sessions-conversation',
  /**
   * One turn, with `data-turn`, `data-malformed`, `data-truncated` (issue #96 -
   * a subset of malformed, never a state beside it), `data-selected` on the turn
   * the État column shows (issue #172), `data-flash` while an arrival lights it,
   * and `data-reread` on the reread row of issue #165, which is a `turn` too: it
   * copies its link, walks under the arrows and opens its layers like any other.
   */
  turn: 'sessions-tour',
  /** The band that opens a step or one of its attempts. */
  stepBand: 'sessions-etape-bandeau',
  /** The « + » that reveals the five accordions of one turn. */
  turnMore: 'sessions-tour-plus',
  /** One layer toggle of the sticky bar, with `data-layer` and `aria-pressed`. */
  layerToggle: 'sessions-couche',
  /** One accordion under a turn, with `data-layer`. */
  layer: 'sessions-tour-couche',
  /** The sticky column holding the snapshot. */
  state: 'sessions-etat',
  /** The box inside that column that scrolls its sections, header and frame left out (issue #226). */
  stateScroll: 'sessions-etat-defilement',
  /** The drawer that renders the frozen protocol in reading. */
  playedProtocol: 'sessions-protocole-joue',
  /** One step inside that drawer, with `data-step-id`. */
  playedStep: 'sessions-protocole-etape',

  /*
    The inside of the five accordions. They came back from the lot that built
    the conversation as literals in its components, because that lot did not
    own this file, and they are hoisted here at the integration for the reason
    the header gives: the e2e suite and the capture scripts recopy these
    strings, so a mark declared where it is drawn is a mark they learn about by
    breaking.
  */
  /** A yellow mark under a reply: a malformed turn, or a flagged incident. */
  turnFlag: 'sessions-tour-marque',
  /**
   * « aucune réponse attendue », in the person's column of the farewell turn
   * (issue #163, §12.3) - where a reply would stand on any other turn.
   */
  turnNoReply: 'sessions-tour-sans-reponse',
  /**
   * The one line of the REREAD row (issue #165, §12.3): « Relecture à l'entrée
   * · relit <étape quittée> » and the objectives it succeeded, or « rien de
   * rempli ».
   */
  turnReread: 'sessions-tour-relecture',
  /**
   * The pages played IN THE CONVERSATION (prolongation.md §7, issue #186). The band
   * that opens one, between the last turn and the farewell, with `data-visit`
   * (0-based, the visit of the page - the card of the parcours it matches),
   * `data-page-id` and `data-bubbles` (always `1`: a page of the thread has one
   * bubble); the bubble itself, an `li` that is NOT a turn - no `data-turn`, no
   * number, no arrow stop - with `data-visit`, `data-page-id`, `data-failed`,
   * `data-click` (`card`, `button`, or absent without a click) and `data-flash`;
   * and its « + », which reveals the two layers a bubble has.
   */
  bubbleBand: 'sessions-page-bandeau',
  bubble: 'sessions-bulle',
  bubbleMore: 'sessions-bulle-plus',
  /** What was shown under the bubble (« Deux cartes montrées · premier choix »). */
  bubbleShown: 'sessions-bulle-montre',
  /** The click of the person's row, with `data-kind` (`card`, `button`); or the line that says there was none. */
  bubbleClick: 'sessions-bulle-clic',
  bubbleNoClick: 'sessions-bulle-sans-clic',
  /** The line of a bubble whose call failed: the page played without a bubble (§4.6). */
  bubbleFailed: 'sessions-bulle-echec',
  /** The pill « jouée dans la conversation » on the card of the parcours, and its link to the bubble. */
  walkBubble: 'sessions-parcours-bulle',
  walkBubbleLink: 'sessions-parcours-bulle-lien',

  /** One objective inside the Objectifs layer, with `data-objective-id`. */
  turnObjective: 'sessions-tour-objectif',
  /** The scribe's verdict on whether the person answered, in the same layer (H4 hybride). */
  turnAnswered: 'sessions-tour-repondu',
  /** One guardrail line of the Garde-fous layer, with `data-guardrail-id`. */
  turnGuardrail: 'sessions-tour-gardefou',
  /** One rule line of the same layer, with `data-rule-id`. */
  turnRule: 'sessions-tour-regle',
  /** One row of the Appels table, with `data-seat`. */
  turnCall: 'sessions-tour-appel',
  /** The raw scribe answer inside the Brut layer. */
  turnRaw: 'sessions-tour-brut',
  /** The button that READS the served inputs, and one seat's input it reveals. */
  turnPrompts: 'sessions-tour-entrees',
  turnPrompt: 'sessions-tour-entree',
  /** The Moteur band: the decision, what produced it, what comes next. */
  turnEngine: 'sessions-tour-moteur',
  /** A criterion's name, pressable, and the card it opens on its frozen direction. */
  criterion: 'sessions-critere',
  /** H47 (§8.2): the sentence a criterion carries when its threshold decided nothing. */
  criterionThresholdIgnored: 'sessions-critere-seuil-ignore',
  criterionCard: 'sessions-critere-fiche',
  /**
   * The frame of an « l'un de » group of criteria (issue #181), with
   * `data-or-group` (the label) and `data-tone` (met, passable, short,
   * unjudged); on a line of the État column and on a card of the drawer it marks
   * the same group. `criterionCarrier` is the pill on the criterion that carried it.
   */
  criterionGroup: 'sessions-critere-groupe',
  criterionCarrier: 'sessions-critere-porteur',
  /**
   * One objective line inside the state column, with `data-objective-id`,
   * `data-status` and `data-target` - the turn a réussi leads to (issue #172).
   */
  stateObjective: 'sessions-etat-objectif',
  /** The floating panel of that line's matter (issue #172), with `data-objective-id`. */
  stateObjectivePanel: 'sessions-etat-objectif-panneau',
  /** « Tour N », which copies the link of its turn (issue #172). */
  turnLink: 'sessions-tour-lien',
} as const;

/**
 * The marks of the Matière tab (§12.4, issue #72).
 *
 * Their own object rather than more members of the one above, because they are
 * read by ONE screen and the other set is read by three. They live in this file
 * all the same: the e2e suite recopies them, and the rule of this folder is that
 * a mark more than one file reads is declared here.
 */
export const MATIERE_TEST_IDS = {
  /** The whole panel. */
  panel: 'sessions-matiere-panneau',
  /** The experience selector - which experience's matter is being read. */
  experience: 'sessions-matiere-experience',
  /** The status filter: réussi, raté, vide, or all of them. */
  status: 'sessions-matiere-statut',
  /** The version filter: one experience version, or all of them. */
  version: 'sessions-matiere-version',
  /** One objective's block, with `data-objective-id`. */
  objective: 'sessions-matiere-objectif',
  /** One extract, with `data-invalidated` and `data-session-id`. */
  extract: 'sessions-matiere-extrait',
  /** The sentence that stands where the extracts would be. */
  empty: 'sessions-matiere-vide',
  /**
   * The sentence above the objectives that says which sessions the search keeps
   * (recherche-sessions.md section 7), with `data-matched` and `data-total`.
   */
  searchNote: 'sessions-matiere-recherche',
} as const;

/* ------------------------------------------------------------ the layers */

/**
 * The six layers of §12.3, in the order their toggles sit in the bar - the
 * names and the order of `CONVERSATION_LAYERS` in `shared`, because the order
 * is part of the address (`?couches=`) and the MCP read tools hand it out too.
 *
 * `etat` is last and apart in every other way: the five before it are
 * accordions under a turn, it is the sticky column. It rides in the same list
 * because it is the same GESTURE for a reader - one switch per kind of detail -
 * and splitting it out would have given the bar five switches and a stray
 * checkbox.
 */
export const SESSION_LAYERS = CONVERSATION_LAYERS;
export type SessionLayer = ConversationLayer;

/** The five that are accordions under a turn - every layer but the column. */
export const TURN_LAYERS = ['objectifs', 'gardefous', 'appels', 'brut', 'moteur'] as const;
export type TurnLayer = (typeof TURN_LAYERS)[number];

/**
 * What each toggle is called. In the catalogue rather than in the bar, the
 * régime of `MODEL_SETTINGS` and of the rail: a second spelling of a label is a
 * second meaning, and these strings are what `getByRole('button', { name })`
 * finds.
 */
export const SESSION_LAYER_LABEL: Record<SessionLayer, string> = {
  moteur: 'Moteur',
  objectifs: 'Objectifs',
  gardefous: 'Garde-fous et règles',
  appels: 'Appels et coût',
  brut: 'Brut',
  etat: 'État',
};

/**
 * The order the accordions are RENDERED in under a turn, which is not the order
 * of the bar.
 *
 * « Moteur en dernier », because the decision of turn N is the hat of the
 * question of turn N+1: it is read between the two, so it is drawn at the
 * bottom of the turn it closes. The bar keeps Moteur first because that is the
 * layer an operator reaches for first.
 */
export const TURN_LAYER_ORDER: readonly TurnLayer[] = TURN_LAYERS;

/* ------------------------------------------------------------- the props */

/** What the sticky bar of §12.3 takes. Controlled, like every surface of this lot. */
export interface LayerBarProps {
  readonly open: ReadonlySet<SessionLayer>;
  readonly onToggle: (layer: SessionLayer) => void;
  readonly onOpenAll: () => void;
  readonly onCloseAll: () => void;
}

/* ------------------------------------------------------------ the review */

/**
 * The marks of the REVIEW page (relecture.md, issue #187), `/admin/sessions/<id>/relecture`.
 *
 * Their own object, like `MATIERE_TEST_IDS`: one screen reads them, and the e2e
 * suite and the capture script recopy them. The prefix is `sessions-review-`
 * (the object is `review` in the code and « Relecture » on screen, D23).
 */
export const REVIEW_TEST_IDS = {
  /** The whole page, with `data-state` (`ready`, `blocked`). */
  screen: 'sessions-review',
  /** What the page says instead of the review, with `data-reason` (an ineligibility). */
  blocked: 'sessions-review-blocked',
  /** The sentence of a gesture that failed or was refused, with `data-code` when it has one. */
  notice: 'sessions-review-notice',
  /** « Replier la relecture » / « Déplier la relecture », with `aria-pressed`. */
  fold: 'sessions-review-fold',
  /** The shortcut « Tester l'analyste sur cette relecture ». */
  trial: 'sessions-review-trial',
  /** Its state, with `data-state` (`idle`, `running`, `rendered`, `failed`). */
  trialState: 'sessions-review-trial-state',
  /** « voir l'essai dans le parcours ». */
  trialLink: 'sessions-review-trial-link',
  /** The sentence of a refusal of the analyst, with `data-code`. */
  trialRefusal: 'sessions-review-trial-refusal',
  /** The sentence under the shortcut when it is closed. */
  trialWhy: 'sessions-review-trial-why',
  /** The five marks above the transcript. */
  legend: 'sessions-review-legend',

  /* --- the transcript --- */
  transcript: 'sessions-review-transcript',
  /** One exchange, with `data-turn`. */
  turn: 'sessions-review-turn',
  /** The interviewer's message - a selectable text, with `data-turn`. */
  question: 'sessions-review-question',
  /** The person's reply - a selectable text, with `data-turn`. */
  reply: 'sessions-review-reply',
  /** « aucune réponse attendue » of the farewell. */
  noReply: 'sessions-review-no-reply',
  /** One passage ranged: `data-ids`, `data-label` (« 1, 6 »), `data-doubt`, `data-noted`. */
  mark: 'sessions-review-mark',
  /** One annotated passage of a question, with `data-verdict` (`good`, `bad`). */
  notedPassage: 'sessions-review-noted-passage',
  /** « Annoter le message » of a turn, with `data-turn`. */
  annotate: 'sessions-review-annotate',
  /** One annotation card under a message, with `data-id` and `data-verdict`. */
  annotation: 'sessions-review-annotation',
  /** The cross of an annotation. */
  annotationRemove: 'sessions-review-annotation-remove',

  /* --- the popover --- */
  /** The floating box, with `data-kind` (`range`, `placed`, `annotate`, `refusal`). */
  popover: 'sessions-review-popover',
  /** The one-sentence refusal of a selection across two messages. */
  refusal: 'sessions-review-refusal',
  /** One case of the list a selection opens, with `data-objective`, `data-doubt`. */
  option: 'sessions-review-option',
  /** The warning of the setting « un seul objectif », above the list. */
  optionWarning: 'sessions-review-option-warning',
  /** One line of a placed passage's popover, with `data-extract`. */
  placed: 'sessions-review-placed',
  /** The name of a case that leads to it. */
  goto: 'sessions-review-goto',
  /** « Va » / « Ne va pas » of the annotation form, with `data-verdict` and `aria-pressed`. */
  verdict: 'sessions-review-verdict',
  annotationNote: 'sessions-review-annotation-note',
  annotationSave: 'sessions-review-annotation-save',
  annotationError: 'sessions-review-annotation-error',

  /* --- the panel --- */
  panel: 'sessions-review-panel',
  /** One tab, with `data-tab` (`rangement`, `noyau`, `notes`, `historique`) and `aria-selected`. */
  tab: 'sessions-review-tab',
  tabPanel: 'sessions-review-tab-panel',
  /** The line that heads the Rangement tab. */
  gridLine: 'sessions-review-grid-line',
  /** One case, with `data-objective`, `data-state`, `data-flash`, and `data-drop` while a drag is over it. */
  case: 'sessions-review-case',
  /** The state of a case, with `data-state`. */
  caseState: 'sessions-review-case-state',
  /** One extract of a case, with `data-extract`, `data-turn`, `data-doubt`, `data-locked`. */
  extract: 'sessions-review-extract',
  /** The « ? » of an extract, with `aria-pressed`; the cross that removes it. */
  doubt: 'sessions-review-doubt',
  unrange: 'sessions-review-unrange',
  /** « aussi en 6 : retirez l'un des deux pour lever le doute ». */
  lock: 'sessions-review-lock',
  /** The note opener of an extract, of a case, of the noyau, and its editor. */
  noteOpen: 'sessions-review-note-open',
  noteInput: 'sessions-review-note-input',
  noteSave: 'sessions-review-note-save',
  noteCancel: 'sessions-review-note-cancel',
  /** A note as it reads. */
  noteText: 'sessions-review-note-text',
  /** « Je valide » / « Je ne valide pas », with `data-verdict`. */
  validate: 'sessions-review-validate',
  /** One validation of a case, with `data-verdict` and `data-current` (false: « avant modification »). */
  validation: 'sessions-review-validation',
  /** The line of a stamp: who, when, « via Claude », with `data-by`. */
  stamp: 'sessions-review-stamp',
  via: 'sessions-review-via',

  /* --- the noyau --- */
  /** One of the six, with `data-noyau` and `aria-pressed`. */
  noyauChoice: 'sessions-review-noyau-choice',
  /** « Ou bien, peut-être », with `data-noyau` and `aria-pressed`. */
  noyauSecond: 'sessions-review-noyau-second',
  /** « Sûr » / « Avec doute », with `data-certainty` and `aria-pressed`. */
  certainty: 'sessions-review-certainty',
  unreadable: 'sessions-review-unreadable',

  /* --- the notes --- */
  /** One free note of the session, with `data-id`. */
  note: 'sessions-review-note',
  noteNew: 'sessions-review-note-new',
  noteAdd: 'sessions-review-note-add',
  noteError: 'sessions-review-note-error',
  noteRemove: 'sessions-review-note-remove',

  /* --- the history --- */
  /** One line of the journal, with `data-op`. */
  event: 'sessions-review-event',
  /** « Remettre ». */
  restore: 'sessions-review-restore',
} as const;
